import type { ExpressAvailabilityStatus } from "@dransay/api";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	getCachedVendorAvailability,
	invalidateExpressAvailabilityCacheExcept,
	setCachedVendorAvailability,
} from "./express-courier-availability-cache";

interface ExpressCourierCheckResponse {
	available: boolean;
	status?: ExpressAvailabilityStatus;
	errorCode?: string;
	result?: {
		success: boolean;
		deliveryProvider?: Array<{ provider: string }>;
	};
	error?: string;
}

interface UseExpressCourierAvailabilityParams {
	vendorId: string | number | undefined;
	postalCode: string | undefined;
	enabled: boolean;
	/**
	 * Opt in to reading the session cache. Cart and checkout must leave this
	 * false so every pre-payment answer stays live (FR-021).
	 */
	preferCache?: boolean;
}

/**
 * Resolves a vendor id usable as a cache key, or null when it is not a plain
 * positive integer — in which case the hook stays on the network only.
 */
function toCacheableVendorId(
	vendorId: string | number | undefined,
): number | null {
	const parsed = Number(vendorId);
	return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

// Use plain fetch instead of useFetcher to return standard JSON responses.
// This avoids turbo-stream encoding issues and aligns with how other hooks
// in the codebase (use-address-suggestions, phone-verification) call API routes.
export function useExpressCourierAvailability({
	vendorId,
	postalCode,
	enabled,
	preferCache = false,
}: UseExpressCourierAvailabilityParams) {
	const canCheck = enabled && !!vendorId && !!postalCode;
	const cacheVendorId = toCacheableVendorId(vendorId);

	const [data, setData] = useState<ExpressCourierCheckResponse | null>(null);
	// Lazy-init to canCheck: when a check will actually run, start "loading"
	// from the very first render so callers never observe a transient
	// status: "unknown", isLoading: false frame before the effect below
	// kicks the real fetch off (which would otherwise look identical to a
	// resolved-but-indeterminate check to a consumer like FR-062's disabled
	// state).
	const [isLoading, setIsLoading] = useState(() => canCheck);
	const abortControllerRef = useRef<AbortController | null>(null);

	const fetchCheck = useCallback(async () => {
		if (!canCheck) {
			setIsLoading(false);
			return;
		}

		const plz = postalCode as string;
		invalidateExpressAvailabilityCacheExcept(plz);

		if (preferCache && cacheVendorId !== null) {
			const cached = getCachedVendorAvailability(plz, cacheVendorId);
			if (cached) {
				setData({
					available: cached.status === "available",
					status: cached.status,
					errorCode: cached.errorCode,
				});
				setIsLoading(false);
				return;
			}
		}

		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
		}

		const controller = new AbortController();
		abortControllerRef.current = controller;
		setIsLoading(true);

		try {
			const params = new URLSearchParams({
				vendorId: String(vendorId),
				postalCode: plz,
			});
			const response = await fetch(
				`/api/express-courier/check?${params.toString()}`,
				{ signal: controller.signal, credentials: "same-origin" },
			);
			if (controller.signal.aborted) return;
			if (!response.ok) {
				throw new Error(`Express courier check failed: ${response.status}`);
			}
			const body = (await response.json()) as ExpressCourierCheckResponse;
			if (controller.signal.aborted) return;
			setData(body);
			if (cacheVendorId !== null && body.status && body.status !== "unknown") {
				setCachedVendorAvailability(plz, cacheVendorId, {
					status: body.status,
					errorCode: body.errorCode,
				});
			}
		} catch {
			if (!controller.signal.aborted) {
				setData({ available: false, status: "unknown" });
			}
		} finally {
			if (!controller.signal.aborted) {
				setIsLoading(false);
				abortControllerRef.current = null;
			}
		}
	}, [canCheck, vendorId, postalCode, preferCache, cacheVendorId]);

	useEffect(() => {
		setData(null);
		fetchCheck();
		return () => {
			abortControllerRef.current?.abort();
		};
	}, [fetchCheck]);

	return useMemo(() => {
		const status: ExpressAvailabilityStatus = data?.status ?? "unknown";
		return {
			status,
			errorCode: data?.errorCode,
			isAvailable: status === "available",
			// The lazy isLoading init only covers the mount render. When
			// canCheck flips false -> true later (e.g. postal code arrives
			// after mount), this render still carries the stale isLoading
			// from before the flip until the effect below fires. Treat an
			// eligible check with no data yet as pending regardless, so
			// consumers never observe a settled-looking "unknown" between
			// the flip and the fetch actually starting.
			isLoading: isLoading || (canCheck && data === null),
		};
	}, [data, isLoading, canCheck]);
}
